import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { HierarchicalFolder, FolderDetails, FolderSortOption } from '@/types/hierarchicalFolders';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';

interface HierarchicalFoldersState {
  // State
  rootFolders: HierarchicalFolder[];
  currentFolder: FolderDetails | null;
  isLoading: boolean;
  error: string | null;
  
  // Filters
  searchQuery: string;
  sortOption: FolderSortOption;
  
  // Actions
  fetchRootFolders: () => Promise<void>;
  fetchFolderDetails: (folderId: string) => Promise<void>;
  createFolder: (name: string, parentId?: string | null) => Promise<HierarchicalFolder>;
  renameFolder: (folderId: string, newName: string) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  deleteFolders: (folderIds: string[]) => Promise<void>;
  moveFolder: (folderId: string, newParentId: string | null) => Promise<void>;
  addVideosToFolder: (folderId: string, videoIds: string[]) => Promise<void>;
  removeVideosFromFolder: (folderId: string, videoIds: string[]) => Promise<void>;
  
  // Navigation
  navigateToFolder: (folderId: string) => Promise<void>;
  navigateBack: () => void;
  
  // Filters
  setSearchQuery: (query: string) => void;
  setSortOption: (option: FolderSortOption) => void;
  
  // Utils
  resetError: () => void;
}

export const useHierarchicalFoldersStore = create<HierarchicalFoldersState>()(
  devtools((set, get) => ({
    // Initial state
    rootFolders: [],
    currentFolder: null,
    isLoading: false,
    error: null,
    searchQuery: '',
    sortOption: 'az',
    
    // Fetch root folders
    fetchRootFolders: async () => {
      set({ isLoading: true, error: null });
      try {
        const { searchQuery, sortOption } = get();
        const result = await hierarchicalFoldersService.listRoot({
          q: searchQuery,
          sort: sortOption
        });
        set({ rootFolders: result.items, isLoading: false });
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao carregar pastas',
          isLoading: false 
        });
      }
    },
    
    // Fetch folder details
    fetchFolderDetails: async (folderId: string) => {
      set({ isLoading: true, error: null });
      try {
        const { searchQuery, sortOption } = get();
        const details = await hierarchicalFoldersService.getFolderDetails(folderId, {
          q: searchQuery,
          sort: sortOption
        });
        set({ currentFolder: details, isLoading: false });
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao carregar pasta',
          isLoading: false 
        });
      }
    },
    
    // Create folder
    createFolder: async (name: string, parentId: string | null = null) => {
      set({ isLoading: true, error: null });
      try {
        console.log('🔵 Store: Creating folder with name:', name, 'parentId:', parentId);
        const newFolder = await hierarchicalFoldersService.createFolder(name, parentId);
        console.log('✅ Store: Folder created:', newFolder);
        
        // Update local state
        if (parentId === null) {
          // Root folder
          set(state => ({
            rootFolders: [...state.rootFolders, newFolder],
            isLoading: false
          }));
        } else {
          // Subfolder - optimistic update
          console.log('🔄 Store: Optimistic update for subfolder in parentId:', parentId);
          const currentFolder = get().currentFolder;
          
          if (currentFolder?.id === parentId) {
            // Add new folder to children immediately
            set(state => ({
              currentFolder: state.currentFolder ? {
                ...state.currentFolder,
                children: [...state.currentFolder.children, newFolder],
                childrenCount: state.currentFolder.childrenCount + 1
              } : null,
              isLoading: false
            }));
            console.log('✅ Store: Subfolder added to UI, total children:', get().currentFolder?.children.length);
          } else {
            set({ isLoading: false });
          }
        }
        
        return newFolder;
      } catch (error) {
        console.error('❌ Store: Error creating folder:', error);
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao criar pasta',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Rename folder
    renameFolder: async (folderId: string, newName: string) => {
      set({ isLoading: true, error: null });
      try {
        await hierarchicalFoldersService.renameFolder(folderId, newName);
        
        // Update local state
        set(state => ({
          rootFolders: state.rootFolders.map(f => 
            f.id === folderId ? { ...f, name: newName } : f
          ),
          currentFolder: state.currentFolder ? {
            ...state.currentFolder,
            name: state.currentFolder.id === folderId ? newName : state.currentFolder.name,
            children: state.currentFolder.children.map(f =>
              f.id === folderId ? { ...f, name: newName } : f
            )
          } : null,
          isLoading: false
        }));
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao renomear pasta',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Delete folder
    deleteFolder: async (folderId: string) => {
      set({ isLoading: true, error: null });
      try {
        await hierarchicalFoldersService.deleteFolder(folderId);
        
        // Update local state
        set(state => ({
          rootFolders: state.rootFolders.filter(f => f.id !== folderId),
          currentFolder: state.currentFolder?.id === folderId 
            ? null 
            : state.currentFolder ? {
                ...state.currentFolder,
                children: state.currentFolder.children.filter(f => f.id !== folderId)
              }
            : null,
          isLoading: false
        }));
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao excluir pasta',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Delete multiple folders
    deleteFolders: async (folderIds: string[]) => {
      set({ isLoading: true, error: null });
      try {
        await hierarchicalFoldersService.deleteFolders(folderIds);
        
        // Update local state
        set(state => ({
          rootFolders: state.rootFolders.filter(f => !folderIds.includes(f.id)),
          currentFolder: state.currentFolder ? {
            ...state.currentFolder,
            children: state.currentFolder.children.filter(f => !folderIds.includes(f.id))
          } : null,
          isLoading: false
        }));
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao excluir pastas',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Move folder
    moveFolder: async (folderId: string, newParentId: string | null) => {
      set({ isLoading: true, error: null });
      try {
        await hierarchicalFoldersService.moveFolder(folderId, newParentId);
        // Reload list/details
        if (get().currentFolder) {
          await get().fetchFolderDetails(get().currentFolder!.id);
        } else {
          await get().fetchRootFolders();
        }
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao mover pasta',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Add videos
    addVideosToFolder: async (folderId: string, videoIds: string[]) => {
      set({ isLoading: true, error: null });
      try {
        await hierarchicalFoldersService.addVideosToFolder(folderId, videoIds);
        // Reload folder details
        await get().fetchFolderDetails(folderId);
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao adicionar vídeos',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Remove videos
    removeVideosFromFolder: async (folderId: string, videoIds: string[]) => {
      set({ isLoading: true, error: null });
      try {
        await hierarchicalFoldersService.removeVideosFromFolder(folderId, videoIds);
        // Reload folder details
        await get().fetchFolderDetails(folderId);
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Erro ao remover vídeos',
          isLoading: false 
        });
        throw error;
      }
    },
    
    // Navigation
    navigateToFolder: async (folderId: string) => {
      await get().fetchFolderDetails(folderId);
    },
    
    navigateBack: () => {
      const currentFolder = get().currentFolder;
      if (currentFolder?.breadcrumb && currentFolder.breadcrumb.length > 1) {
        const parentFolder = currentFolder.breadcrumb[currentFolder.breadcrumb.length - 2];
        get().navigateToFolder(parentFolder.id);
      } else {
        set({ currentFolder: null });
      }
    },
    
    // Filters
    setSearchQuery: (query: string) => {
      set({ searchQuery: query });
    },
    
    setSortOption: (option: FolderSortOption) => {
      set({ sortOption: option });
    },
    
    // Utils
    resetError: () => set({ error: null })
  }))
);
